import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import {
  BookOpen, Play, FileText, Download, Clock, CheckCircle2,
  ChevronRight, Lock, Search, Filter, GraduationCap,
  Video, File, Award, BarChart3, Users, Shield,
  Brain, Zap, Monitor, ChevronDown, ExternalLink,
  BookMarked, Star, TrendingUp
} from 'lucide-react';
import './TrainingPortal.css';

const iconMap = {
  Brain, Zap, Shield, Award, Lock, TrendingUp,
  BookOpen, Video, FileText, File, Users,
  BarChart3, Monitor, GraduationCap,
};

const TrainingPortal = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [trainingModules, setTrainingModules] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedModule, setExpandedModule] = useState(null);
  const sectionRefs = useRef([]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('tp-in-view');
          }
        });
      },
      { threshold: 0.15 }
    );

    sectionRefs.current.filter(Boolean).forEach((el) => observer.observe(el));
    return () => {
      sectionRefs.current.filter(Boolean).forEach((el) => observer.unobserve(el));
    };
  }, [isAuthenticated, loading]);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/profile', {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserName(data.firstName || 'User');
        await fetchTrainingMaterials();
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingMaterials = async () => {
    try {
      const response = await fetch('/api/training-materials', {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const modulesWithIcons = (data.modules || []).map((mod) => ({
          ...mod,
          icon: iconMap[mod.icon] || BookOpen,
        }));
        setTrainingModules(modulesWithIcons);
      } else if (response.status === 401 || response.status === 403) {
        navigate('/signin');
      } else {
        setFetchError('Failed to load training materials.');
      }
    } catch (error) {
      setFetchError('Error loading training materials. Please try again.');
    }
  };

  const categories = [
    { id: 'all', label: 'All Courses', icon: BookOpen },
    { id: 'wellness', label: 'Wellness', icon: TrendingUp },
  ];

  const filteredModules = trainingModules.filter((module) => {
    const matchesCategory = activeCategory === 'all' || module.category === activeCategory;
    const matchesSearch =
      module.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return '#34C759';
      case 'Intermediate': return '#FF9500';
      case 'Advanced': return '#FF3B30';
      default: return '#86868B';
    }
  };

  const getMaterialIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={16} />;
      case 'document': return <FileText size={16} />;
      default: return <File size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="tp-loading-screen">
        <div className="tp-loading-spinner"></div>
        <p>Loading Training Portal...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="tp-auth-wall">
        <Helmet>
          <title>Training Portal - Erasys</title>
        </Helmet>
        <motion.div
          className="tp-auth-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="tp-auth-icon-wrapper">
            <Lock size={48} strokeWidth={1.5} />
          </div>
          <h1 className="tp-auth-title">Training Portal</h1>
          <p className="tp-auth-description">
            Access exclusive training materials, courses, and certifications designed for Erasys platform users.
            Sign in to unlock your learning journey.
          </p>
          <div className="tp-auth-features">
            <div className="tp-auth-feature">
              <Video size={20} />
              <span>Video Courses</span>
            </div>
            <div className="tp-auth-feature">
              <FileText size={20} />
              <span>Documentation</span>
            </div>
            <div className="tp-auth-feature">
              <Award size={20} />
              <span>Certifications</span>
            </div>
          </div>
          <button className="tp-auth-signin-btn" onClick={() => navigate('/signin')}>
            Sign In to Access
            <ChevronRight size={18} />
          </button>
          <p className="tp-auth-signup-text">
            Don't have an account?{' '}
            <span className="tp-auth-link" onClick={() => navigate('/signup')}>
              Sign up for free
            </span>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="tp-container">
      <Helmet>
        <title>Training Portal - Erasys</title>
        <meta name="description" content="Access training materials, courses, and certifications for Erasys products." />
      </Helmet>

      {/* Hero Section */}
      <section className="tp-hero">
        <motion.div
          className="tp-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="tp-hero-badge">
            <GraduationCap size={16} />
            <span>Training Portal</span>
          </div>
          <h1 className="tp-hero-title">
            Welcome back, {userName}.
          </h1>
          <p className="tp-hero-subtitle">
            Explore courses, documentation, and certifications to master the Erasys ecosystem.
          </p>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className="tp-stats-row"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="tp-stat-card">
            <BookOpen size={22} strokeWidth={1.5} />
            <div className="tp-stat-info">
              <span className="tp-stat-number">{trainingModules.length}</span>
              <span className="tp-stat-label">Courses</span>
            </div>
          </div>
          <div className="tp-stat-card">
            <Video size={22} strokeWidth={1.5} />
            <div className="tp-stat-info">
              <span className="tp-stat-number">
                {trainingModules.reduce((acc, m) => acc + m.materials.filter(mat => mat.type === 'video').length, 0)}
              </span>
              <span className="tp-stat-label">Videos</span>
            </div>
          </div>
          <div className="tp-stat-card">
            <FileText size={22} strokeWidth={1.5} />
            <div className="tp-stat-info">
              <span className="tp-stat-number">
                {trainingModules.reduce((acc, m) => acc + m.materials.filter(mat => mat.type === 'document').length, 0)}
              </span>
              <span className="tp-stat-label">Documents</span>
            </div>
          </div>
          <div className="tp-stat-card">
            <Clock size={22} strokeWidth={1.5} />
            <div className="tp-stat-info">
              <span className="tp-stat-number">7h+</span>
              <span className="tp-stat-label">Content</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Search & Filter */}
      <section className="tp-filter-section">
        <div className="tp-search-bar">
          <Search size={18} className="tp-search-icon" />
          <input
            type="text"
            className="tp-search-input"
            placeholder="Search courses, topics, or materials..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="tp-category-tabs">
          {categories.map((cat) => {
            const IconComp = cat.icon;
            return (
              <button
                key={cat.id}
                className={`tp-category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <IconComp size={16} />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Course Grid */}
      <section className="tp-courses-section">
        {fetchError ? (
          <div className="tp-no-results">
            <Shield size={40} strokeWidth={1.5} />
            <h3>Unable to load materials</h3>
            <p>{fetchError}</p>
            <button className="tp-footer-btn" onClick={fetchTrainingMaterials} style={{ marginTop: '16px' }}>
              Try Again
            </button>
          </div>
        ) : filteredModules.length === 0 ? (
          <div className="tp-no-results">
            <Search size={40} strokeWidth={1.5} />
            <h3>No courses found</h3>
            <p>Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className="tp-courses-grid">
            {filteredModules.map((module, index) => {
              const IconComp = module.icon;
              const isExpanded = expandedModule === module.id;

              return (
                <motion.div
                  key={module.id}
                  className={`tp-course-card ${isExpanded ? 'expanded' : ''}`}
                  ref={(el) => (sectionRefs.current[index] = el)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  layout
                >
                  {/* Card Header */}
                  <div className="tp-card-header">
                    <div className="tp-card-icon" style={{ backgroundColor: `${module.color}12` }}>
                      <IconComp size={24} color={module.color} strokeWidth={1.5} />
                    </div>
                    <div className="tp-card-meta">
                      <span
                        className="tp-level-badge"
                        style={{ color: getLevelColor(module.level), backgroundColor: `${getLevelColor(module.level)}15` }}
                      >
                        {module.level}
                      </span>
                      <span className="tp-card-category">
                        {categories.find((c) => c.id === module.category)?.label}
                      </span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="tp-card-body">
                    <h3 className="tp-card-title">{module.title}</h3>
                    <p className="tp-card-description">{module.description}</p>

                    <div className="tp-card-stats">
                      <div className="tp-card-stat">
                        <Clock size={14} />
                        <span>{module.duration}</span>
                      </div>
                      <div className="tp-card-stat">
                        <BookMarked size={14} />
                        <span>{module.lessons} lessons</span>
                      </div>
                    </div>
                  </div>

                  {/* Expand Toggle */}
                  <button
                    className="tp-expand-btn"
                    onClick={() => setExpandedModule(isExpanded ? null : module.id)}
                  >
                    <span>{isExpanded ? 'Hide Materials' : 'View Materials'}</span>
                    <ChevronDown
                      size={16}
                      className={`tp-expand-icon ${isExpanded ? 'rotated' : ''}`}
                    />
                  </button>

                  {/* Expanded Materials */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        className="tp-materials-list"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {module.materials.map((material, matIndex) => (
                          <div key={matIndex} className="tp-material-item">
                            <div className="tp-material-icon" style={{ color: module.color }}>
                              {getMaterialIcon(material.type)}
                            </div>
                            <div className="tp-material-info">
                              <span className="tp-material-title">{material.title}</span>
                              <span className="tp-material-meta">
                                {material.duration || material.format}
                              </span>
                            </div>
                            <button 
                              className="tp-material-action"
                              onClick={() => material.url && window.open(material.url, '_blank')}
                              style={material.url ? { cursor: 'pointer' } : {}}
                            >
                              {material.type === 'video' ? (
                                <Play size={14} />
                              ) : (
                                <Download size={14} />
                              )}
                            </button>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Footer CTA */}
      <section className="tp-footer-section">
        <div className="tp-footer-card">
          <div className="tp-footer-content">
            <GraduationCap size={32} strokeWidth={1.5} />
            <h3>Need custom training?</h3>
            <p>Contact our team for personalized training programs tailored to your organization's needs.</p>
            <button className="tp-footer-btn" onClick={() => navigate('/contact')}>
              Get in Touch
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrainingPortal;
